import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrmData } from '../../contexts/CrmDataContext';
import { accountDetailPath } from '../../lib/portal';
import { taskAccountDisplay } from '../../lib/taskAccountDisplay';
import { Modal } from '../ui/Modal';
import { TaskAttachmentsField } from '../tasks/TaskAttachmentsField';
import type { Client, MeetingMinute, Task } from '../../types/crm';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { taskDisplayStatus, taskStatusModifier } from '../../utils/taskHelpers';
import { fleetTaskDescriptionForDisplay } from '../../lib/fleetTasks';

interface TaskDetailModalProps {
  open: boolean;
  task: Task | null;
  client?: Client;
  /** When false, show account name/code but do not link to account details. */
  canOpenAccount?: boolean;
  meetingMinute?: MeetingMinute | null;
  ownerName: string;
  canEdit: boolean;
  completing?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDone?: () => void;
}

export function TaskDetailModal({
  open,
  task,
  client,
  canOpenAccount = true,
  meetingMinute,
  ownerName,
  canEdit,
  completing = false,
  onClose,
  onEdit,
  onDone,
}: TaskDetailModalProps) {
  const { username } = useCrmData();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  if (!task) return null;

  const display = taskDisplayStatus(task);
  const notes = fleetTaskDescriptionForDisplay(task.description);
  const account = taskAccountDisplay(task, client);
  const isDone = display === 'Done';
  const allowAccountLink = Boolean(client) && canOpenAccount;
  const contactPerson = task.contact_person?.trim() ?? '';
  const contactPhone = task.contact_phone?.trim() ?? '';
  const address = task.address?.trim() ?? '';

  return (
    <Modal title="Task details" open={open} onClose={onClose}>
        <div className="task-detail-modal">
          <div className="task-detail-modal__badges">
            <span className={`task-status ${taskStatusModifier(display)}`}>{display}</span>
            {task.status && task.status !== display && (
              <span className="task-detail-modal__status-raw">{task.status}</span>
            )}
          </div>

          <h3 className="task-detail-modal__title">{task.title}</h3>

          <dl className="task-detail-modal__meta">
            {task.due_date && (
              <>
                <dt>Due</dt>
                <dd className={display === 'Due' ? 'task-detail-modal__overdue' : undefined}>
                  {formatSingaporeDateTime(task.due_date)}
                </dd>
              </>
            )}
            <dt>Account</dt>
            <dd>
              {client && allowAccountLink ? (
                <Link to={accountDetailPath(client.code)} onClick={onClose}>
                  {client.company_name}
                  {client.code ? (
                    <span className="task-detail-modal__code"> ({client.code})</span>
                  ) : null}
                </Link>
              ) : client || account.isProspect ? (
                <>
                  {account.name}
                  {account.code && account.code !== account.name ? (
                    <span className="task-detail-modal__code"> ({account.code})</span>
                  ) : null}
                </>
              ) : (
                'No account linked'
              )}
            </dd>
            {account.outletName ? (
              <>
                <dt>Place</dt>
                <dd>{account.outletName}</dd>
              </>
            ) : null}
            {contactPerson ? (
              <>
                <dt>Contact person</dt>
                <dd>{contactPerson}</dd>
              </>
            ) : null}
            {contactPhone ? (
              <>
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${contactPhone.replace(/\s+/g, '')}`}>{contactPhone}</a>
                </dd>
              </>
            ) : null}
            {address ? (
              <>
                <dt>Address</dt>
                <dd className="task-detail-modal__address">{address}</dd>
              </>
            ) : null}
            <dt>Owner</dt>
            <dd>{ownerName}</dd>
            {meetingMinute ? (
              <>
                <dt>Meeting minutes</dt>
                <dd>{meetingMinute.title}</dd>
              </>
            ) : null}
          </dl>

          <div className="task-detail-modal__notes">
            <h4 className="task-detail-modal__notes-label">Notes</h4>
            {notes ? (
              <p className="task-detail-modal__notes-body">{notes}</p>
            ) : (
              <p className="task-detail-modal__notes-empty">No notes for this task.</p>
            )}
          </div>

          <TaskAttachmentsField
            taskId={task.id}
            canEdit={canEdit}
            uploadedBy={username}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
            compact
          />

          <div className="task-detail-modal__actions">
            {canEdit && !isDone && onDone ? (
              <button type="button" className="btn-primary" disabled={completing} onClick={onDone}>
                {completing ? 'Saving…' : 'Complete task'}
              </button>
            ) : null}
            {canEdit && (
              <button type="button" className="btn-secondary" onClick={onEdit}>
                Edit task
              </button>
            )}
          </div>
        </div>
      </Modal>
  );
}
